import React, { useState } from "react";

export default function QuizCreator() {
  const [title, setTitle] = useState("");
  const [displayMode, setDisplayMode] = useState("single"); // or "all"
  const [validationKey, setValidationKey] = useState("");
  const [questions, setQuestions] = useState([
    { text: "", options: ["", ""], correct: 0 },
  ]);

  // Add a new question
  const addQuestion = () => {
    setQuestions([...questions, { text: "", options: ["", ""], correct: 0 }]);
  };

  // Remove a question by index
  const removeQuestion = (index) => {
    if (questions.length <= 1) return; // at least 1 question
    setQuestions(questions.filter((_, i) => i !== index));
  };

  // Update question text
  const updateQuestionText = (index, text) => {
    const q = [...questions];
    q[index].text = text;
    setQuestions(q);
  };

  // Add option to a question (max 8)
  const addOption = (qIndex) => {
    const q = [...questions];
    if (q[qIndex].options.length >= 8) return;
    q[qIndex].options.push("");
    setQuestions(q);
  };

  // Remove option from a question (min 2)
  const removeOption = (qIndex, oIndex) => {
    const q = [...questions];
    if (q[qIndex].options.length <= 2) return;
    q[qIndex].options.splice(oIndex, 1);

    // If removed option was the correct one, reset correct to 0
    if (q[qIndex].correct >= q[qIndex].options.length) {
      q[qIndex].correct = 0;
    }
    setQuestions(q);
  };

  // Update option text
  const updateOptionText = (qIndex, oIndex, text) => {
    const q = [...questions];
    q[qIndex].options[oIndex] = text;
    setQuestions(q);
  };

  // Set correct option index
  const setCorrectOption = (qIndex, oIndex) => {
    const q = [...questions];
    q[qIndex].correct = oIndex;
    setQuestions(q);
  };

  // Submit quiz to backend
  const handleSubmit = () => {
    if (!title.trim()) {
      alert("Please enter quiz title.");
      return;
    }
    for (let i = 0; i < questions.length; i++) {
      if (!questions[i].text.trim()) {
        alert(`Please enter text for question #${i + 1}`);
        return;
      }
      if (questions[i].options.some((opt) => !opt.trim())) {
        alert(`Please fill all options for question #${i + 1}`);
        return;
      }
    }
    if (!validationKey.trim()) {
      alert("Please enter validation key.");
      return;
    }

    const data = {
      title,
      display_mode: displayMode,
      validation_key: validationKey,
      questions,
    };

    console.log("Submitting quiz data:", data);

    fetch("https://yesedacademy.com/database/quiz.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then(async (res) => {
        const text = await res.text();
        try {
          const result = JSON.parse(text);
          console.log("Response from server:", result);
          if (result.status === "success") {
            alert("Quiz created successfully!");
            // Reset form or redirect if needed
          } else {
            alert("Error: " + result.message);
          }
        } catch (err) {
          console.error("Invalid JSON from server:", text);
          alert("Server returned invalid response.");
        }
      })
      .catch((error) => {
        console.error("Fetch error:", error);
        alert("Failed to submit quiz.");
      });
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow rounded mt-8">
      <h2 className="text-2xl font-bold mb-4">Create New Quiz</h2>

      <label className="block mb-2 font-semibold">Quiz Title:</label>
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="border rounded p-2 w-full mb-4"
      />

      <label className="block mb-2 font-semibold">Display Mode:</label>
      <select
        value={displayMode}
        onChange={(e) => setDisplayMode(e.target.value)}
        className="border rounded p-2 w-full mb-4"
      >
        <option value="single">One question at a time</option>
        <option value="all">All questions on one page</option>
      </select>

      <label className="block mb-2 font-semibold">Validation Key:</label>
      <input
        type="text"
        value={validationKey}
        onChange={(e) => setValidationKey(e.target.value)}
        className="border rounded p-2 w-full mb-6"
        placeholder="Key to protect quiz integrity"
      />

      {questions.map((q, qIndex) => (
        <div key={qIndex} className="mb-8 border p-4 rounded bg-gray-50">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-semibold text-lg">Question {qIndex + 1}</h3>
            <button
              onClick={() => removeQuestion(qIndex)}
              className="text-red-600 hover:text-red-800"
              title="Remove Question"
            >
              &times;
            </button>
          </div>

          <input
            type="text"
            placeholder="Enter question text"
            value={q.text}
            onChange={(e) => updateQuestionText(qIndex, e.target.value)}
            className="border rounded p-2 w-full mb-4"
          />

          <div className="mb-2">Options:</div>
          {q.options.map((opt, oIndex) => (
            <div key={oIndex} className="flex items-center mb-2">
              <input
                type="radio"
                name={`correct-${qIndex}`}
                checked={q.correct === oIndex}
                onChange={() => setCorrectOption(qIndex, oIndex)}
                className="mr-2"
              />
              <input
                type="text"
                placeholder={`Option ${oIndex + 1}`}
                value={opt}
                onChange={(e) => updateOptionText(qIndex, oIndex, e.target.value)}
                className="border rounded p-2 flex-grow"
              />
              {q.options.length > 2 && (
                <button
                  onClick={() => removeOption(qIndex, oIndex)}
                  className="ml-2 text-red-600 hover:text-red-800 font-bold"
                  title="Remove Option"
                  type="button"
                >
                  &times;
                </button>
              )}
            </div>
          ))}
          {q.options.length < 8 && (
            <button
              onClick={() => addOption(qIndex)}
              className="mt-2 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
              type="button"
            >
              + Add Option
            </button>
          )}
        </div>
      ))}

      <div className="flex justify-between">
        <button
          onClick={addQuestion}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          type="button"
        >
          + Add Question
        </button>

        <button
          onClick={handleSubmit}
          className="px-6 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          type="button"
        >
          Submit Quiz
        </button>
      </div>
    </div>
  );
}
